import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, ArrowRight, Check, Clock3, Mail, MailCheck, Plus, Trash2, Users } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { OnboardingProgressCard, managerOnboardingSteps } from '../components/OnboardingProgressCard'

type EmailWorkModel = 'personal' | 'shared' | 'both'
type OnboardingStep = 1 | 2 | 3

type TeamDraft = {
  id: string
  name: string
  plannedMemberCount: number
  startTime: string
  endTime: string
  workingDays: number[]
}

const models: Array<{
  value: EmailWorkModel
  title: string
  description: string
  icon: typeof Mail
}> = [
  {
    value: 'personal',
    title: 'Personal work inboxes',
    description: 'Your organisation enables each employee work inbox centrally after the provider is connected.',
    icon: Mail,
  },
  {
    value: 'shared',
    title: 'Shared team inboxes',
    description: 'Your team works from inboxes such as sales@company.com or support@company.com.',
    icon: Users,
  },
  {
    value: 'both',
    title: 'Both personal and shared inboxes',
    description: 'Employees use their own work inboxes and can also be assigned shared team inboxes.',
    icon: MailCheck,
  },
]

const workingDayOptions = [
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
  { value: 0, label: 'Sun' },
]

const suggestedTeamNames = ['Support', 'Sales', 'Customer Success', 'Accounts', 'Retention', 'Onboarding']

function makeTeam(index: number): TeamDraft {
  return {
    id: `team-${Date.now()}-${index}`,
    name: suggestedTeamNames[index] ?? `Team ${index + 1}`,
    plannedMemberCount: 5,
    startTime: '08:00',
    endTime: '17:00',
    workingDays: [1, 2, 3, 4, 5],
  }
}

export function Onboarding() {
  const navigate = useNavigate()
  const [organisationName, setOrganisationName] = useState('')
  const [fullName, setFullName] = useState('')
  const [emailWorkModel, setEmailWorkModel] = useState<EmailWorkModel>('personal')
  const [teams, setTeams] = useState<TeamDraft[]>([makeTeam(0)])
  const [currentStep, setCurrentStep] = useState<OnboardingStep>(1)
  const [busy, setBusy] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    async function prepare() {
      if (!supabase) {
        setError('Supabase is not configured.')
        setBusy(false)
        return
      }

      const { data: { user } } = await supabase.auth.getUser()
      if (!user) {
        navigate('/login?returnTo=/onboarding', { replace: true })
        return
      }

      const { data: membership } = await supabase
        .from('memberships')
        .select('organisation_id,role')
        .eq('user_id', user.id)
        .order('created_at', { ascending: true })
        .limit(1)
        .maybeSingle()

      if (membership?.organisation_id) {
        navigate(membership.role === 'member' ? '/employee' : '/app', { replace: true })
        return
      }

      setFullName(String(user.user_metadata?.full_name ?? ''))
      setOrganisationName(String(user.user_metadata?.organisation_name ?? ''))
      setBusy(false)
    }

    prepare()
  }, [navigate])

  const duplicateTeamName = useMemo(() => {
    const names = teams.map(team => team.name.trim().toLowerCase()).filter(Boolean)
    return new Set(names).size !== names.length
  }, [teams])

  const currentOverallStep = currentStep + 1

  function updateTeam(id: string, patch: Partial<TeamDraft>) {
    setTeams(current => current.map(team => team.id === id ? { ...team, ...patch } : team))
  }

  function addTeam() {
    setTeams(current => current.length >= 12 ? current : [...current, makeTeam(current.length)])
  }

  function setTeamCount(count: number) {
    const nextCount = Math.max(1, Math.min(12, count))
    setTeams(current => {
      if (nextCount === current.length) return current
      if (nextCount < current.length) return current.slice(0, nextCount)
      const next = [...current]
      while (next.length < nextCount) next.push(makeTeam(next.length))
      return next
    })
  }

  function removeTeam(id: string) {
    setTeams(current => current.length === 1 ? current : current.filter(team => team.id !== id))
  }

  function toggleWorkingDay(teamId: string, day: number) {
    setTeams(current => current.map(team => {
      if (team.id !== teamId) return team
      const hasDay = team.workingDays.includes(day)
      return {
        ...team,
        workingDays: hasDay
          ? team.workingDays.filter(value => value !== day)
          : [...team.workingDays, day].sort((a, b) => a - b)
      }
    }))
  }

  function validateStep(step: OnboardingStep): boolean {
    if (step === 1) {
      if (!fullName.trim()) {
        setError('Enter your name to continue.')
        return false
      }
      if (!organisationName.trim()) {
        setError('Enter the organisation name to continue.')
        return false
      }
      return true
    }

    if (step === 2) {
      if (!emailWorkModel) {
        setError('Choose how your organisation handles customer email.')
        return false
      }
      return true
    }

    const cleanTeams = teams.map(team => ({
      ...team,
      name: team.name.trim(),
      plannedMemberCount: Math.max(1, Number(team.plannedMemberCount) || 1),
    }))

    if (cleanTeams.some(team => !team.name)) {
      setError('Give every customer-email team a name.')
      return false
    }

    if (duplicateTeamName) {
      setError('Each customer-email team needs a different name.')
      return false
    }

    if (cleanTeams.some(team => team.plannedMemberCount < 1)) {
      setError('Enter at least one planned member for every team.')
      return false
    }

    if (cleanTeams.some(team => team.workingDays.length === 0)) {
      setError('Choose at least one working day for every team.')
      return false
    }

    if (cleanTeams.some(team => team.endTime <= team.startTime)) {
      setError('Each team’s closing time must be later than its opening time.')
      return false
    }

    return true
  }

  function goToStep(step: OnboardingStep) {
    if (step === currentStep) return

    if (step > currentStep) {
      const stepsToValidate: OnboardingStep[] = currentStep === 1
        ? (step === 2 ? [1] : [1, 2])
        : [2]

      const valid = stepsToValidate.every(validateStep)
      if (!valid) return
    }

    setError('')
    setCurrentStep(step)
  }

  function goNext() {
    if (!validateStep(currentStep)) return
    setError('')
    setCurrentStep(current => Math.min(3, current + 1) as OnboardingStep)
  }

  function goBack() {
    setError('')
    setCurrentStep(current => Math.max(1, current - 1) as OnboardingStep)
  }

  async function finish() {
    if (!supabase) return
    if (!validateStep(3)) return

    const cleanTeams = teams.map(team => ({
      ...team,
      name: team.name.trim()
    }))

    setBusy(true)
    setError('')

    const { error: provisionError } = await supabase.rpc('provision_mail_percent_workspace', {
      organisation_name: organisationName.trim(),
      owner_display_name: fullName.trim() || null,
      p_email_work_model: emailWorkModel,
      p_teams: cleanTeams.map(team => ({
        name: team.name,
        planned_member_count: team.plannedMemberCount,
        start_time: team.startTime,
        end_time: team.endTime,
        working_days: team.workingDays,
      }))
    })

    setBusy(false)

    if (provisionError) {
      setError(provisionError.message)
      return
    }

    navigate('/app/team?setup=complete', { replace: true })
  }

  if (busy && !organisationName && !fullName) {
    return <div className="auth-loading"><span className="blue-spinner auth-spinner"/></div>
  }

  return <div className="auth-page onboarding-shell-page">
    <div className="auth-brand"><img className="mail-percent-logo app-logo" src="/images/brand/mail-percent-logo.png" alt="Mail Percent"/></div>

    <div className="onboarding-shell">
      <aside className="onboarding-overview-panel">
        <OnboardingProgressCard
          steps={managerOnboardingSteps}
          currentStep={currentOverallStep}
          sectionLabel="Manager setup"
          heading="4 steps to launch your workspace"
        />
      </aside>

      <main className="auth-card onboarding-wizard-card">
        <div className="onboarding-header">
          <div>
            <span className="eyebrow">Organisation setup</span>
            <h1>Create your Mail Percent workspace</h1>
            <p>Step through the setup below. Your progress is visible and you can move between steps as you complete them.</p>
          </div>
          <div className="onboarding-header-progress">
            <strong>{currentOverallStep} / 4</strong>
            <span>{managerOnboardingSteps[currentOverallStep - 1]?.title}</span>
          </div>
        </div>

        <div className="onboarding-mobile-steps onboarding-mobile-steps-four">
          {managerOnboardingSteps.map((item, index) => {
            const overallStep = index + 1
            const isComplete = overallStep < currentOverallStep
            const isActive = overallStep === currentOverallStep
            return <button
              key={item.title}
              type="button"
              className={isActive ? 'active' : isComplete ? 'complete' : ''}
              aria-current={isActive ? 'step' : undefined}
              aria-disabled={overallStep === 1}
              onClick={() => {
                if (overallStep >= 2) goToStep((overallStep - 1) as OnboardingStep)
              }}
            >
              <span>{isComplete ? <Check size={12}/> : overallStep}</span>
              <strong>{item.title}</strong>
            </button>
          })}
        </div>

        <form onSubmit={event => event.preventDefault()} className="auth-form onboarding-wizard-form">
          {currentStep === 1 && (
            <section className="onboarding-step-panel">
              <div className="onboarding-step-panel-head">
                <div className="onboarding-step-badge">2</div>
                <div>
                  <strong>Organisation details</strong>
                  <small>Tell Mail Percent which organisation this manager workspace belongs to.</small>
                </div>
              </div>

              <div className="onboarding-two-field-grid">
                <label>Your name<input className="search-input" value={fullName} onChange={e => setFullName(e.target.value)} required /></label>
                <label>Organisation name<input className="search-input" value={organisationName} onChange={e => setOrganisationName(e.target.value)} required /></label>
              </div>
            </section>
          )}

          {currentStep === 2 && (
            <section className="onboarding-step-panel">
              <div className="onboarding-step-panel-head">
                <div className="onboarding-step-badge">3</div>
                <div>
                  <strong>Email workspace model</strong>
                  <small>Choose how your employees handle customer email so the workspace starts with the right structure.</small>
                </div>
              </div>

              <fieldset className="inbox-model-fieldset onboarding-model-fieldset onboarding-model-fieldset-modern">
                <legend>How does your organisation handle customer email?</legend>
                <div className="inbox-model-options onboarding-model-grid-modern">
                  {models.map(model => {
                    const Icon = model.icon
                    return <label className={`inbox-model-option onboarding-model-card ${emailWorkModel === model.value ? 'selected' : ''}`} key={model.value}>
                      <input type="radio" name="emailWorkModel" value={model.value} checked={emailWorkModel === model.value} onChange={() => setEmailWorkModel(model.value)} />
                      <div className="onboarding-model-icon"><Icon size={20}/></div>
                      <div className="onboarding-model-copy">
                        <strong>{model.title}</strong>
                        <span>{model.description}</span>
                      </div>
                    </label>
                  })}
                </div>
              </fieldset>
            </section>
          )}

          {currentStep === 3 && (
            <section className="onboarding-step-panel onboarding-team-setup-section">
              <div className="onboarding-step-panel-head onboarding-team-heading-row">
                <div className="onboarding-step-heading-inline">
                  <div className="onboarding-step-badge">4</div>
                  <div>
                    <strong>Customer-email teams</strong>
                    <small>Set up the teams that communicate with customers by email, together with their planned members and schedules.</small>
                  </div>
                </div>
                <button className="button secondary" type="button" onClick={addTeam} disabled={teams.length >= 12}><Plus size={15}/>Add team</button>
              </div>

              <div className="onboarding-team-count-line onboarding-team-count-line-modern">
                <Users size={18}/>
                <div><strong>How many customer-email teams will use Mail Percent?</strong><span>Member numbers are planning figures, not hard limits.</span></div>
                <label className="onboarding-team-number-field">
                  <span>Teams</span>
                  <select value={teams.length} onChange={event => setTeamCount(Number(event.target.value))}>
                    {Array.from({ length: 12 }, (_, index) => index + 1).map(count => <option value={count} key={count}>{count}</option>)}
                  </select>
                </label>
              </div>

              <div className="onboarding-team-list">
                {teams.map((team, index) => <article className="onboarding-team-card onboarding-team-card-modern" key={team.id}>
                  <div className="onboarding-team-card-head">
                    <div><span>Team {index + 1}</span><strong>{team.name || 'Unnamed team'}</strong></div>
                    {teams.length > 1 && <button type="button" className="onboarding-team-remove" onClick={() => removeTeam(team.id)} aria-label={`Remove ${team.name || `team ${index + 1}`}`}><Trash2 size={15}/></button>}
                  </div>

                  <div className="onboarding-team-fields">
                    <label>Team name<input value={team.name} onChange={event => updateTeam(team.id, { name: event.target.value })} placeholder="Support" required/></label>
                    <label>Planned members<input type="number" min="1" max="999" value={team.plannedMemberCount} onChange={event => updateTeam(team.id, { plannedMemberCount: Math.max(1, Number(event.target.value) || 1) })}/></label>
                    <label>Opens<input type="time" value={team.startTime} onChange={event => updateTeam(team.id, { startTime: event.target.value })}/></label>
                    <label>Closes<input type="time" value={team.endTime} onChange={event => updateTeam(team.id, { endTime: event.target.value })}/></label>
                  </div>

                  <div className="onboarding-working-days">
                    <div className="onboarding-working-days-label"><Clock3 size={15}/><span>Working days</span></div>
                    <div className="onboarding-day-toggle-grid">
                      {workingDayOptions.map(day => <button
                        type="button"
                        key={day.value}
                        className={team.workingDays.includes(day.value) ? 'active' : ''}
                        onClick={() => toggleWorkingDay(team.id, day.value)}
                        aria-pressed={team.workingDays.includes(day.value)}
                      >{day.label}</button>)}
                    </div>
                  </div>
                </article>)}
              </div>
            </section>
          )}

          {error && <p className="auth-error">{error}</p>}

          <div className="onboarding-wizard-actions">
            <button type="button" className="button secondary" onClick={goBack} disabled={currentStep === 1 || busy}><ArrowLeft size={16}/>Back</button>

            <div className="onboarding-wizard-actions-right">
              <div>
                <strong>{currentStep < 3 ? 'Continue setup' : 'Ready to create the workspace?'}</strong>
                <span>{currentStep < 3 ? 'You can move through each setup step clearly.' : 'You can adjust team standards and employee assignments later.'}</span>
              </div>

              {currentStep < 3 ? (
                <button type="button" className="button primary large" onClick={goNext}>Next<ArrowRight size={16}/></button>
              ) : (
                <button type="button" className="button primary large" disabled={busy} onClick={finish}>{busy ? 'Creating workspace…' : 'Create organisation'}<ArrowRight size={16}/></button>
              )}
            </div>
          </div>
        </form>
      </main>
    </div>
  </div>
}
